import { Theme } from 'mytours-read-api-typescript'
import { AppMetaData, DEVICE_PLATFORM_KEY_MOBILE_WEB, getConfiguration, getDirectApi } from 'mytours-read-common'

import strings, { languageStrings } from '../../strings'
import { getDataFromLocalStorage, hasNewThemingSystem, setDataToLocalStorage } from './index'

/** Per-locale UI string overrides extracted from a project theme: `{ locale: { string_key: text } }`. */
export type ThemeStringOverrides = { [locale: string]: { [key: string]: string } }

const themeStringsDiskKey = (projectId: string): string => `${projectId}_theme_strings`

/** Longest the first render is held back waiting for the theme fetch when no cache exists. */
const FIRST_FETCH_WAIT_LIMIT_MS = 2000

let fetchStarted = false
let fetchCompleted = false
let fetchSettled: Promise<void> = Promise.resolve()
let appliedJson: string | undefined

/** Test-only: module state would otherwise leak the first test's fetch into the others. */
export function resetThemeStringsSync(): void {
	fetchStarted = false
	fetchCompleted = false
	fetchSettled = Promise.resolve()
}

const bundledBaseLanguages = new Set(Object.keys(languageStrings).map(locale => locale.split('-')[0]))

/**
 * Pulls per-locale overrides out of a theme payload. `settings.strings` is an array of single-key
 * objects: `[{ start_tour: { value: { en: 'Begin', fr: 'Commencer' } } }]`. Entries scoped to a
 * specific themable and locales no bundle can resolve are skipped.
 */
export function extractThemeStringOverrides(theme: Theme): ThemeStringOverrides {
	const overrides: ThemeStringOverrides = {}

	for (const entry of theme.settings?.strings || []) {
		for (const [key, setting] of Object.entries(entry)) {
			const { value, themable_type, themable_id } = (setting || {}) as {
				value?: { [locale: string]: unknown }
				themable_type?: string
				themable_id?: number
			}
			if (!value || themable_type || themable_id) continue

			for (const [locale, text] of Object.entries(value)) {
				if (typeof text !== 'string') continue
				if (!languageStrings[locale] && !bundledBaseLanguages.has(locale)) continue
				if (!overrides[locale]) overrides[locale] = {}
				overrides[locale][key] = text
			}
		}
	}

	return overrides
}

/**
 * Replaces the localization content with the bundled strings merged with the given overrides.
 * Overrides are keyed by the project's supported languages, while the displayed bundle can be a
 * regional variant of one of them (browser language `en-GB` on an `en` project), so base-language
 * overrides also apply to variant bundles, with exact-locale overrides taking precedence.
 * `setContent` resets the active language to the browser one, so it is restored afterwards.
 */
export function applyThemeStringOverrides(overrides: ThemeStringOverrides): void {
	const overridesJson = JSON.stringify(overrides)
	if (overridesJson === (appliedJson ?? '{}')) return
	appliedJson = overridesJson

	const merged: typeof languageStrings = {}
	for (const locale of Object.keys(languageStrings)) {
		const base = overrides[locale.split('-')[0]]
		const exact = overrides[locale]
		merged[locale] = base || exact ? { ...languageStrings[locale], ...base, ...exact } : languageStrings[locale]
	}

	const language = strings.getLanguage()
	strings.setContent(merged)
	strings.setLanguage(language)
}

async function fetchThemeStringOverrides(): Promise<ThemeStringOverrides> {
	const configuration = getConfiguration()
	// the theme endpoint serves the CSS representation unless JSON is requested explicitly,
	// and the generated client mistypes the parsed JSON response as `string`
	const theme = configuration.shouldUsePublishedContent()
		? await configuration.stqryBunnyReadApi.fetchTheme(configuration.getAccountId())
		: ((await getDirectApi().projectTheme(DEVICE_PLATFORM_KEY_MOBILE_WEB, undefined, {
				headers: { Accept: 'application/json' },
			})) as unknown as Theme)
	return extractThemeStringOverrides(theme)
}

/**
 * Applies the admin-customised UI strings delivered with the project theme. A fresh theme is
 * fetched once per page load, applied and cached for the next one. Failures leave the current
 * strings in place.
 *
 * The strings library is not reactive: components read `strings.*` at render time and nothing
 * re-renders when the content is swapped underneath them. The sagas dispatch
 * `loadedMetaDataComplete` — which lets the first screen render — only after the promise returned
 * here settles, so it resolves as soon as the freshest strings quickly available are applied:
 * immediately when cached overrides exist (or the project has none), otherwise when the fetch
 * settles, held back at most {@link FIRST_FETCH_WAIT_LIMIT_MS}. A fetch slower than that still
 * applies late and only shows on the next mount, rather than stalling the app on a bad network.
 */
export function syncThemeStrings(metaData: AppMetaData): Promise<void> {
	if (!hasNewThemingSystem(metaData)) return Promise.resolve()

	const diskKey = themeStringsDiskKey(String(metaData.project_id))

	const cacheApplied = getDataFromLocalStorage(diskKey)
		.then((cached: ThemeStringOverrides | null) => {
			// a fetched theme is fresher than the cache, never apply the cache on top of it
			if (cached && !fetchCompleted) applyThemeStringOverrides(cached)
			return cached != null
		})
		.catch(() => false)

	if (!fetchStarted) {
		fetchStarted = true

		fetchSettled = fetchThemeStringOverrides()
			.then(overrides => {
				fetchCompleted = true
				applyThemeStringOverrides(overrides)
				return setDataToLocalStorage(diskKey, overrides)
			})
			.catch(() => undefined) // offline or theme endpoint unavailable — keep current strings
	}

	return cacheApplied.then(hadCache => {
		if (hadCache) return
		return Promise.race([fetchSettled, new Promise<void>(resolve => setTimeout(resolve, FIRST_FETCH_WAIT_LIMIT_MS))])
	})
}
